import { request } from '@umijs/max';
import type { ImageCanvasGroup } from '@/pages/GenerationMedia/ai-chat-image/components/chatCanvas/types/imageCanvas';

type ImageLayerSplitStatus = 'queued' | 'running' | 'completed' | 'failed';
type ImageLayerSplitResponse<T> = { code: number; msg?: string; data?: T };

// 图层拆分：提交任务
export async function submitImageLayerSplit(
  data: {
    session_id: string;
    request_id: string;
    source_group: ImageCanvasGroup;
    layer_split_prompt?: string;
  },
  options?: { [key: string]: any },
) {
  return request<ImageLayerSplitResponse<{ task_id: string; status: ImageLayerSplitStatus }>>(`/agent-api/v1/agent/image/layer/split`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

// 图层拆分：查询结果
export async function queryImageLayerSplitStatus(
  data: {
    task_id: string;
  },
  options?: { [key: string]: any },
) {
  return request<ImageLayerSplitResponse<{ task_id: string; status: ImageLayerSplitStatus; groups?: ImageCanvasGroup[]; error_message?: string }>>(`/agent-api/v1/agent/image/layer/split/status`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

// 模型参数接口
export async function getModelList(
  params: {
    media_type: string;
    quick_tab?: string;
    session_id?: any;
  },
  options?: { [key: string]: any },
) {
  return request<API.ApiResponse>(`/agent-api/v1/agent/chat/model/list`, {
    method: 'GET',
    params,
    headers: { 'X-Agent-Name': 'muse' },
    ...(options || {}),
  });
}

// 取消聊天
export async function cancelChat(
  data: {
    task_id: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.ApiResponse>(`/agent-api/v1/agent/chat/cancel`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

// 缩放图片
export async function scaleImage(
  data: {
    task_id: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.ApiResponse>(`/agent-api/v1/agent/chat/scale`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export type BrandMediaAsset = {
  http_url?: string;
  thumbnail_http_url?: string;
  thumbnail_url?: string;
  type?: string;
  url?: string;
  [key: string]: any;
};

export type BrandStyle = {
  visual_identity: {
    visual_style: string;
    color_palette: string;
    mood: string;
    negative_constraint: string;
  };
  motion_audio: {
    motion: string;
    audio_vibe: string;
    voice_tone: string;
    editing_pace: {
      option_list?: { label?: string; value?: string }[];
      selected?: string;
    };
  };
};
export type BrandItem = {
  id: string;
  brand_name: string;
  brand_description: string;
  brand_assets: {
    logo_image_url?: BrandMediaAsset[];
    reference_image_urls?: BrandMediaAsset[];
  };
  created_at?: string;
  enabled?: boolean;
  motion_audio?: BrandStyle['motion_audio'];
  updated_at?: string;
  visual_identity?: BrandStyle['visual_identity'];
  youtube_urls?: string[];
};

export type BrandListData = {
  items: BrandItem[];
};

export type BrandPayload = {
  agent_name: string;
  brand_assets: {
    logo_image_url: BrandMediaAsset[];
    reference_image_urls: BrandMediaAsset[];
  };
  brand_description: string;
  brand_name: string;
  media_type: string;
  motion_audio?: BrandStyle['motion_audio'];
  project_id?: string;
  visual_identity?: BrandStyle['visual_identity'];
  youtube_urls: string[];
};

// 提取档案
export async function archiveExtract(
  data: {
    media_type: string;
    payload: {
      brand_name: string;
      brand_description: string;
      logo_image_url: string;
      reference_image_urls: string[];
      youtube_urls: string[];
    };
    meta: {
      client_ts: number;
    };
  },
  options?: { [key: string]: any },
) {
  return request<API.ApiResponse>(`/pub-api/v1/archive/extract`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

// 品牌提取
export async function brandExtract(
  data: {
    agent_name: string;
    brand_assets: {
      logo_image_url: BrandMediaAsset[];
      reference_image_urls: BrandMediaAsset[];
    };
    brand_name: string;
    brand_description: string;
    media_type: string;
    youtube_urls: string[];
  },
  options?: { [key: string]: any },
) {
  return request<API.ApiResponse<BrandStyle>>(`/pub-api/v1/archive/brand_extract`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

// 添加品牌
export async function addBrand(data: BrandPayload, options?: { [key: string]: any }) {
  return request<API.ApiResponse<BrandItem>>(`/pub-api/v1/archive/brand_upsert`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

// 删除品牌
export async function deleteBrand(
  data: {
    project_id: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.ApiResponse>(`/pub-api/v1/archive/brand_delete/${data.project_id}`, {
    method: 'DELETE',
    data,
    ...(options || {}),
  });
}

// 更新品牌
export async function updateBrand(data: BrandPayload & { project_id: string }, options?: { [key: string]: any }) {
  return request<API.ApiResponse>(`/pub-api/v1/archive/brand_update/${data.project_id}`, {
    method: 'PUT',
    data,
    ...(options || {}),
  });
}

// 获取品牌列表
export async function getBrandList(
  params: {
    agent_name?: string;
    media_type?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.ApiResponse<BrandListData>>(`/pub-api/v1/archive/brand_list`, {
    params,
    method: 'GET',
    ...(options || {}),
  });
}

// 获取品牌数量
export async function getBrandCount(
  params: {
    agent_name?: string;
    media_type?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.ApiResponse>(`/pub-api/v1/archive/brand_count`, {
    params,
    method: 'GET',
    ...(options || {}),
  });
}
// 获取技能列表
export async function getSkillList(options?: { [key: string]: any }) {
  return request<API.ApiResponse>(`/pub-api/v1/skill/list`, {
    method: 'GET',
    ...(options || {}),
  });
}
